/*
 * Calzados Celita: comportamiento propio del tema (paneles, pie, cesta).
 * Sin dependencias. Se carga con defer en todas las páginas.
 */
(() => {
  const root = document.documentElement;
  const desktop = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Paneles (<dialog class="cc-sheet">) ---------- */

  let lastTrigger = null;

  function openSheet(dialog, trigger) {
    if (!dialog || dialog.open) return;
    lastTrigger = trigger || document.activeElement;
    // El foco va primero al propio diálogo (atributo autofocus), que ya está en pantalla.
    // Si fuera al título mientras el panel aún está fuera, el móvil saltaría para enseñarlo.
    dialog.showModal();
    dialog.scrollTop = 0;
    root.setAttribute('scroll-lock', '');
    requestAnimationFrame(() => dialog.classList.add('is-visible'));
    const focusTitle = () => dialog.querySelector('h2[tabindex="-1"]')?.focus({ preventScroll: true });
    if (reducedMotion.matches) focusTitle();
    else setTimeout(focusTitle, 320);
  }

  function closeSheet(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-visible');
    const finish = () => {
      dialog.close();
      if (!document.querySelector('dialog.cc-sheet[open]')) root.removeAttribute('scroll-lock');
      if (lastTrigger && lastTrigger.isConnected) lastTrigger.focus({ preventScroll: true });
    };
    if (reducedMotion.matches) return finish();
    const panel = dialog.querySelector('.cc-sheet__panel');
    let done = false;
    const once = () => {
      if (done) return;
      done = true;
      finish();
    };
    panel.addEventListener('transitionend', once, { once: true });
    setTimeout(once, 400);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cc-open]');
    if (opener) {
      event.preventDefault();
      openSheet(document.getElementById(opener.dataset.ccOpen), opener);
      return;
    }
    const closer = event.target.closest('[data-cc-close]');
    if (closer) {
      event.preventDefault();
      closeSheet(closer.closest('dialog'));
    }
  });

  // Esc: cerramos con animación en vez del cierre seco del navegador
  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target.matches?.('dialog.cc-sheet')) {
        event.preventDefault();
        closeSheet(event.target);
      }
    },
    true
  );

  // Si se pasa a escritorio con el menú móvil abierto, se cierra
  desktop.addEventListener('change', () => {
    const menu = document.getElementById('cc-menu');
    if (desktop.matches && menu?.open) closeSheet(menu);
  });

  /* ---------- Pie: acordeones en móvil, columnas abiertas en escritorio ---------- */

  function syncFooter() {
    document.querySelectorAll('.cc-foot__col').forEach((details) => {
      details.open = desktop.matches;
    });
  }
  syncFooter();
  desktop.addEventListener('change', syncFooter);
  document.addEventListener('shopify:section:load', syncFooter);

  // En móvil, abrir un grupo cierra los demás
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (desktop.matches || !details.matches?.('.cc-foot__col') || !details.open) return;
      document.querySelectorAll('.cc-foot__col[open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    },
    true
  );

  /* ---------- Colección: filtros y orden sin recargar la página ---------- */

  const plp = document.querySelector('[data-cc-plp]');
  if (plp) {
    const sectionId = plp.dataset.section;
    let controller = null;

    const paramsFromForm = (form) => {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        if (String(value).trim() !== '') params.append(key, value);
      }
      return params;
    };

    async function refresh(url, sourceForm) {
      controller?.abort();
      controller = new AbortController();
      const fetchUrl = new URL(url, location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);
      plp.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(fetchUrl, { signal: controller.signal });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('[data-cc-plp]');
        if (!next) return;

        plp.querySelector('[data-cc-results]').replaceWith(next.querySelector('[data-cc-results]'));
        for (const selector of ['[data-cc-count-label]', '[data-cc-view-label]']) {
          const target = plp.querySelector(selector);
          const source = next.querySelector(selector);
          if (target && source) target.textContent = source.textContent;
        }
        const badgeHost = plp.querySelector('[data-cc-open^="cc-filters"]');
        const newBadgeHost = next.querySelector('[data-cc-open^="cc-filters"]');
        if (badgeHost && newBadgeHost) badgeHost.innerHTML = newBadgeHost.innerHTML;

        // Formularios de filtros: el que se está usando solo actualiza recuentos; los demás se sustituyen
        const forms = plp.querySelectorAll('[data-cc-facets]');
        const newForms = next.querySelectorAll('[data-cc-facets]');
        forms.forEach((form, i) => {
          const fresh = newForms[i];
          if (!fresh) return;
          if (form === sourceForm) {
            fresh.querySelectorAll('[data-cc-count]').forEach((el) => {
              const old = form.querySelector(`[data-cc-count="${CSS.escape(el.dataset.ccCount)}"]`);
              if (old) old.textContent = el.textContent;
            });
            fresh.querySelectorAll('[data-cc-count-for]').forEach((input) => {
              const old = form.querySelector(`[data-cc-count-for="${CSS.escape(input.dataset.ccCountFor)}"]`);
              if (!old) return;
              old.disabled = input.disabled;
              old.closest('label')?.classList.toggle('is-disabled', input.disabled && old.closest('.cc-check') !== null);
              old.closest('label')?.classList.toggle('is-soldout', input.disabled && old.closest('.cc-size') !== null);
            });
          } else {
            form.innerHTML = fresh.innerHTML;
          }
        });

        const clean = new URL(url, location.origin);
        clean.searchParams.delete('section_id');
        history.replaceState({}, '', clean.pathname + clean.search);
      } catch (error) {
        if (error.name !== 'AbortError') location.href = url;
      } finally {
        plp.removeAttribute('aria-busy');
      }
    }

    const currentSort = () => new URL(location.href).searchParams.get('sort_by');

    plp.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('[data-cc-sort]')) {
        const url = new URL(location.href);
        url.searchParams.set('sort_by', target.value);
        url.searchParams.delete('page');
        plp.querySelectorAll('[data-cc-sort]').forEach((select) => (select.value = target.value));
        plp.querySelectorAll('[data-cc-facets] input[name="sort_by"]').forEach((input) => (input.value = target.value));
        refresh(url.pathname + url.search);
        return;
      }
      const form = target.closest('[data-cc-facets]');
      if (!form) return;
      const params = paramsFromForm(form);
      const sort = currentSort();
      if (sort && !params.has('sort_by')) params.set('sort_by', sort);
      refresh(`${form.getAttribute('action')}?${params}`, form);
    });

    plp.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-cc-facets]');
      if (!form) return;
      event.preventDefault();
      closeSheet(form.closest('dialog'));
    });

    plp.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-cc-link]');
      if (!link || event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      refresh(link.getAttribute('href'));
    });
  }

  /* ---------- Cesta (panel) ---------- */

  const cartDialog = () => document.getElementById('cc-cart');

  function updateCartCount(count) {
    document.querySelectorAll('[data-cc-cart-count]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.hidden = count === 0;
    });
    document.querySelectorAll('[data-cc-cart-link]').forEach((link) => {
      link.setAttribute('aria-label', count === 1 ? 'Cesta, 1 artículo' : `Cesta, ${count} artículos`);
    });
  }

  function renderCart(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cc-cart-inner]');
    const dialog = cartDialog();
    const current = dialog?.querySelector('[data-cc-cart-inner]');
    if (!fresh || !current) return;
    const scroll = current.querySelector('.cc-cart__body')?.scrollTop ?? 0;
    current.replaceWith(fresh);
    const body = fresh.querySelector('.cc-cart__body');
    if (body) body.scrollTop = scroll;
    if (dialog.classList.contains('is-visible')) fresh.style.transition = 'none';
    updateCartCount(Number(fresh.dataset.count || 0));
    // La cesta ha cambiado: el aviso de «ya tienes todas las unidades» deja de valer
    document.querySelectorAll('[data-cc-add-error]').forEach((box) => (box.hidden = true));
  }

  async function cartRequest(url, payload) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: 'cc-cart-drawer' }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'No se ha podido actualizar la cesta.');
    return data;
  }

  // Abrir la cesta desde la cabecera (en la página /cart se deja el enlace normal)
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cc-cart-link]');
    if (!link || !cartDialog() || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    openSheet(cartDialog(), link);
  });

  // Cambiar cantidades o eliminar
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cc-cart] [data-cc-qty]');
    if (!button) return;
    const line = button.closest('[data-line]');
    const errorBox = cartDialog().querySelector('[data-cc-cart-error]');
    line.setAttribute('aria-busy', 'true');
    try {
      const data = await cartRequest('/cart/change.js', { line: Number(line.dataset.line), quantity: Number(button.dataset.ccQty) });
      renderCart(data.sections?.['cc-cart-drawer']);
      cartDialog().querySelector('#cc-cart-title')?.focus({ preventScroll: true });
    } catch (error) {
      line.removeAttribute('aria-busy');
      if (errorBox) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      }
    }
  });

  /* ---------- Ficha de producto ---------- */

  const pdp = document.querySelector('[data-cc-pdp]');
  if (pdp) {
    const data = JSON.parse(pdp.querySelector('[data-cc-product-json]').textContent);
    const form = pdp.querySelector('[data-cc-product-form]');
    const idInput = form.querySelector('[data-cc-variant-id]');
    const fieldsets = [...form.querySelectorAll('[data-cc-option]')];
    const addButton = form.querySelector('[data-cc-add]');
    const addLabel = form.querySelector('[data-cc-add-label]');
    const addError = form.querySelector('[data-cc-add-error]');
    const sizeError = form.querySelector('[data-cc-size-error]');
    const stockNote = form.querySelector('[data-cc-stock]');
    const bar = pdp.querySelector('[data-cc-buybar]');
    const barAdd = pdp.querySelector('[data-cc-bar-add]');
    const barVariant = pdp.querySelector('[data-cc-bar-variant]');
    const barPrice = pdp.querySelector('[data-cc-bar-price]');
    const priceBox = pdp.querySelector('[data-cc-price]');
    const track = pdp.querySelector('[data-cc-track]');
    const thumbs = [...pdp.querySelectorAll('[data-cc-thumb]')];

    // Opciones fijas (con un solo valor) que no tienen selector
    const fixed = data.variants[0].options.map((value, i) => (fieldsets.some((f) => Number(f.dataset.ccOption) === i) ? null : value));

    const selected = () =>
      fixed.map((value, i) => {
        if (value !== null) return value;
        const checked = form.querySelector(`[data-cc-option="${i}"] input:checked`);
        return checked ? checked.value : null;
      });

    const findVariant = (opts) => data.variants.find((v) => v.options.every((value, i) => value === opts[i]));

    function filterGallery(color) {
      if (!track || data.colorIndex === null) return;
      const slides = [...track.children];
      const hasColor = slides.some((slide) => slide.dataset.color === color);
      let firstVisible = null;
      slides.forEach((slide) => {
        slide.hidden = hasColor && slide.dataset.color !== '' && slide.dataset.color !== color;
        if (!slide.hidden && !firstVisible) firstVisible = slide;
      });
      thumbs.forEach((thumb) => {
        thumb.hidden = hasColor && thumb.dataset.color !== '' && thumb.dataset.color !== color;
        thumb.removeAttribute('aria-current');
      });
      thumbs.find((t) => !t.hidden)?.setAttribute('aria-current', 'true');
      track.scrollTo({ left: 0, behavior: 'instant' });
    }

    function update({ fromColor = false } = {}) {
      const opts = selected();

      // Disponibilidad de cada talla para el color elegido
      if (data.sizeIndex !== null) {
        form.querySelectorAll(`[data-cc-option="${data.sizeIndex}"] input`).forEach((input) => {
          const probe = [...opts];
          probe[data.sizeIndex] = input.value;
          const variant = findVariant(probe);
          const soldOut = !variant || !variant.available;
          input.disabled = soldOut;
          input.closest('label').classList.toggle('is-soldout', soldOut);
          if (soldOut && input.checked) input.checked = false;
        });
      }

      const now = selected();
      fieldsets.forEach((fieldset) => {
        const i = Number(fieldset.dataset.ccOption);
        const label = fieldset.querySelector('[data-cc-option-value]');
        if (label) label.textContent = now[i] ?? 'elige una';
      });

      const complete = now.every((v) => v !== null);
      const variant = complete ? findVariant(now) : null;
      idInput.value = variant ? variant.id : '';

      // Precio (con todas las tallas al mismo precio, se usa cualquier variante del color)
      const priced = variant || data.variants.find((v) => data.colorIndex === null || v.options[data.colorIndex] === now[data.colorIndex]) || data.variants[0];
      priceBox.querySelector('.cc-price__now').textContent = priced.priceText;
      const was = priceBox.querySelector('.cc-price__was');
      if (was) was.remove();
      if (priced.compareText) {
        priceBox.querySelector('.cc-price__now').insertAdjacentHTML('afterend', `<span class="cc-price__was">${priced.compareText}</span>`);
      }
      if (barPrice) barPrice.textContent = priced.priceText;

      // Últimas unidades
      if (stockNote) {
        if (variant && variant.available && variant.qty !== null && variant.qty <= data.lowStock) {
          stockNote.innerHTML = `<span class="cc-badge cc-badge--low">${variant.qty === 1 ? 'Queda 1 unidad en esta talla' : `Quedan ${variant.qty} unidades en esta talla`}</span>`;
          stockNote.hidden = false;
        } else {
          stockNote.hidden = true;
        }
      }

      const anyAvailable = data.variants.some((v) => v.available && (data.colorIndex === null || v.options[data.colorIndex] === now[data.colorIndex]));
      const soldOut = (variant && !variant.available) || !anyAvailable;
      addButton.disabled = soldOut;
      if (barAdd) barAdd.disabled = soldOut;
      addLabel.textContent = soldOut ? 'Agotado' : 'Añadir a la cesta';
      if (barAdd) barAdd.textContent = soldOut ? 'Agotado' : 'Añadir a la cesta';

      if (barVariant) {
        const parts = [];
        if (data.colorIndex !== null) parts.push(now[data.colorIndex]);
        if (data.sizeIndex !== null) parts.push(now[data.sizeIndex] ? `Talla ${now[data.sizeIndex]}` : 'Elige talla');
        barVariant.textContent = parts.join(' · ');
      }

      if (variant) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
        if (sizeError) sizeError.hidden = true;
      }
      if (fromColor && data.colorIndex !== null) filterGallery(now[data.colorIndex]);
      addError.hidden = true;
    }

    form.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      const fieldset = event.target.closest('[data-cc-option]');
      update({ fromColor: Number(fieldset.dataset.ccOption) === data.colorIndex });
    });

    async function addToCart() {
      if (!idInput.value) {
        if (sizeError) {
          sizeError.hidden = false;
          const group = form.querySelector(`[data-cc-option="${data.sizeIndex}"]`);
          group.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
          group.querySelector('input:not(:disabled)')?.focus({ preventScroll: true });
        }
        return;
      }
      addButton.setAttribute('aria-busy', 'true');
      addButton.disabled = true;
      try {
        const res = await cartRequest('/cart/add.js', { items: [{ id: Number(idInput.value), quantity: 1 }] });
        renderCart(res.sections?.['cc-cart-drawer']);
        openSheet(cartDialog(), addButton);
      } catch (error) {
        addError.textContent = /disponible|available|stock|cantidad/i.test(error.message)
          ? 'Ya tienes en la cesta todas las unidades que quedan de esta talla.'
          : error.message;
        addError.hidden = false;
      } finally {
        addButton.removeAttribute('aria-busy');
        addButton.disabled = false;
      }
    }

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      addToCart();
    });
    barAdd?.addEventListener('click', addToCart);

    // Guía de tallas: abre el desplegable y baja hasta él
    pdp.querySelector('[data-cc-open-guide]')?.addEventListener('click', (event) => {
      const guide = document.getElementById('cc-acc-guia');
      if (!guide) return;
      event.preventDefault();
      guide.open = true;
      guide.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      guide.querySelector('summary').focus({ preventScroll: true });
    });

    // Miniaturas (escritorio)
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const visibleThumbs = thumbs.filter((t) => !t.hidden);
        const index = visibleThumbs.indexOf(thumb);
        const slide = [...track.children].filter((s) => !s.hidden)[index];
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        thumbs.forEach((t) => t.removeAttribute('aria-current'));
        thumb.setAttribute('aria-current', 'true');
      });
    });

    // Barra fija del móvil: aparece cuando el botón principal sale de la pantalla
    if (bar && 'IntersectionObserver' in window) {
      const main = form.querySelector('[data-cc-main-actions]');
      new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }).observe(main);
    }

    update();
    if (data.colorIndex !== null) thumbs.find((t) => !t.hidden)?.setAttribute('aria-current', 'true');
  }

  /* ---------- Preferencias de cookies (aviso de cookies de Shopify) ---------- */

  const cookieButtons = document.querySelectorAll('[data-cc-cookie-prefs]');
  if (cookieButtons.length) {
    let tries = 0;
    const reveal = () => {
      if (window.privacyBanner?.showPreferences) {
        cookieButtons.forEach((button) => {
          button.hidden = false;
          button.addEventListener('click', () => window.privacyBanner.showPreferences());
        });
      } else if (tries++ < 20) {
        setTimeout(reveal, 500);
      }
    };
    window.addEventListener('load', reveal);
  }

  window.Celita = Object.assign(window.Celita || {}, { openSheet, closeSheet, updateCartCount });
})();
